(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.TBP_CALCULATOR_ENGINE = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const VERSION = '0.7-copy-polish-rc';
  const DISCRETE_CARB = [30, 45, 60, 75, 90];
  const SODIUM_MG_PER_MMOL = 23;

  const FLUID_FALLBACK = {
    low: [250, 450],
    medium: [350, 600],
    high: [500, 750],
    very_high: [650, 900]
  };
  const FLUID_CLASSES = ['low', 'medium', 'high', 'very_high'];
  const PRACTICED_FLUID_HIGH = {
    unknown: null,
    lt500: 500,
    '500_750': 750,
    '750_1000': 1000,
    '1000_1250': 1250,
    gt1250: 1500
  };

  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }
  function round5(n) { return Math.round(n / 5) * 5; }
  function round10(n) { return Math.round(n / 10) * 10; }
  function roundRange(range, step = 10) {
    if (!range) return null;
    return [Math.round(range[0] / step) * step, Math.round(range[1] / step) * step];
  }
  function asNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  function minDefined(...vals) {
    const xs = vals.filter(v => Number.isFinite(v));
    return xs.length ? Math.min(...xs) : null;
  }
  function rangeText(range, unit) {
    if (!range) return '—';
    if (range[0] === range[1]) return `${round5(range[0])} ${unit}`;
    return `${round5(range[0])}–${round5(range[1])} ${unit}`;
  }
  function formatMinutes(min) {
    const m = Math.max(0, Math.round(min));
    const h = Math.floor(m / 60), r = m % 60;
    return h ? `${h}:${String(r).padStart(2, '0')}` : `${r} perc`;
  }
  function envFlags(tempC, humidityPct) {
    const t = asNumber(tempC) ?? 20;
    const h = asNumber(humidityPct);
    return {
      cool: t <= 10,
      moderate: t >= 11 && t <= 20,
      warm: t >= 21 && t <= 27,
      hot: t >= 28,
      highHumidity: h !== null && h >= 70 && t >= 20,
      hotHumid: t >= 28 || (h !== null && h >= 70 && t >= 20)
    };
  }

  function carbBase(durationMin, intensity) {
    const d = asNumber(durationMin) || 0;
    const col = intensity === 'controlled' ? 0 : intensity === 'moderate' ? 1 : 2;
    if (d < 45) return { kind: 'short', totalRangeG: [0, 0], baseGph: 0 };
    if (d < 60) {
      if (col === 0) return { kind: 'short', totalRangeG: [0, 0], baseGph: 0 };
      if (col === 1) return { kind: 'short', totalRangeG: [0, 20], baseGph: null };
      return { kind: 'short', totalRangeG: [20, 30], baseGph: null };
    }
    let row;
    if (d < 90) row = [30, 30, 45];
    else if (d < 120) row = [30, 45, 60];
    else if (d < 150) row = [45, 60, 60];
    else if (d < 240) row = [60, 75, 90];
    else row = [60, 75, 90];
    return { kind: 'rate', baseGph: row[col], totalRangeG: null };
  }

  function carbEventRange(durationMin) {
    const d = asNumber(durationMin) || 0;
    if (d < 60) return [0, 30];
    if (d < 90) return [20, 45];
    if (d < 120) return [30, 60];
    if (d < 150) return [45, 75];
    return [60, 90];
  }

  function parsePracticedCarb(value, durationContextMin) {
    if (value === null || value === undefined || value === '' || value === 'unknown') {
      const d = asNumber(durationContextMin) || 0;
      const cap = d < 90 ? 30 : d < 120 ? 30 : d < 150 ? 45 : 60;
      return { kind: 'unknown', capGph: cap, confidence: 'BECSÜLT' };
    }
    if (String(value) === 'lt30') return { kind: 'lt30', rangeGph: [20, 30], confidence: 'BEGYAKOROLT TARTOMÁNY' };
    const n = asNumber(value);
    if (n === null) return parsePracticedCarb('unknown', durationContextMin);
    const cap = Math.max(0, Math.min(90, n));
    return { kind: 'known', capGph: cap, confidence: 'BEGYAKOROLT' };
  }

  function nextTrainingTarget(executionGph, baseGph) {
    if (!Number.isFinite(executionGph) || !Number.isFinite(baseGph) || executionGph >= baseGph) return null;
    const next = DISCRETE_CARB.find(x => x > executionGph) ?? 90;
    return Math.min(next, baseGph);
  }

  function computeCarb({ durationContextMin, executionDurationMin, intensity, practiced }) {
    const contextDur = asNumber(durationContextMin) || 0;
    const execDur = asNumber(executionDurationMin ?? durationContextMin) || 0;
    const base = carbBase(contextDur, intensity);
    if (base.kind === 'short') {
      const tr = base.totalRangeG;
      return {
        kind: 'short', base, executionGph: null, executionRangeGph: null,
        totalRangeG: tr, totalG: tr[0] === tr[1] ? tr[0] : null,
        confidence: 'ÁLTALÁNOS KIINDULÓPONT', mtcRequired: false,
        developmentTargetGph: null, eventBasedTargetGph: null,
        display: tr[0] === tr[1] && tr[0] === 0 ? '0 g' : `${tr[0]}–${tr[1]} g összesen`,
        warning: tr[1] > 0 ? 'Rövid terhelésnél ez opcionális teljes mennyiség, nem kötelező g/óra-cél.' : null
      };
    }

    const p = parsePracticedCarb(practiced, contextDur);
    let executionGph = null, executionRangeGph = null;
    if (p.kind === 'lt30') {
      executionRangeGph = [Math.min(p.rangeGph[0], base.baseGph), Math.min(p.rangeGph[1], base.baseGph)];
      if (executionRangeGph[0] > executionRangeGph[1]) executionRangeGph[0] = executionRangeGph[1];
    } else {
      executionGph = Math.min(base.baseGph, p.capGph);
    }
    const durH = execDur / 60;
    const totalRangeG = executionRangeGph ? [executionRangeGph[0] * durH, executionRangeGph[1] * durH] : null;
    const totalG = executionGph !== null ? executionGph * durH : null;
    const devRef = executionGph ?? executionRangeGph?.[1] ?? null;
    const developmentTargetGph = nextTrainingTarget(devRef, base.baseGph);
    const high = executionGph ?? executionRangeGph?.[1] ?? 0;
    return {
      kind: 'rate', base, practiced: p, executionGph, executionRangeGph,
      totalG, totalRangeG: totalRangeG ? roundRange(totalRangeG, 5) : null,
      confidence: p.confidence,
      mtcRequired: high > 60,
      highCarbWarning: high >= 75,
      developmentTargetGph,
      eventBasedTargetGph: base.baseGph,
      display: executionRangeGph ? rangeText(executionRangeGph, 'g/óra') : `${round5(executionGph)} g/óra`,
      warning: high >= 75 ? 'Magas szénhidrátcél: ezt fokozatosan, edzésen gyakorold, és versenyen csak tolerált stratégiát használj.' : null
    };
  }

  function measuredFluidBase(rateLph, durationMin) {
    const r = asNumber(rateLph) || 0;
    const d = asNumber(durationMin) || 0;
    let row = r < 0.6 ? 0 : r < 1.0 ? 1 : r <= 1.5 ? 2 : 3;
    let matrix;
    if (d <= 90) matrix = [[200,400],[300,500],[400,650],[500,750]];
    else if (d <= 240) matrix = [[250,450],[350,600],[500,750],[750,1000]];
    else matrix = [[250,450],[350,600],[500,750],[600,1000]];
    return { row, matrix, range: matrix[row].slice() };
  }

  function shiftMeasuredBand(base, shift) {
    const idx = clamp(base.row + shift, 0, base.matrix.length - 1);
    return base.matrix[idx].slice();
  }
  function shiftFallbackClass(className, shift) {
    let idx = FLUID_CLASSES.indexOf(className);
    if (idx < 0) idx = 1;
    idx = clamp(idx + shift, 0, FLUID_CLASSES.length - 1);
    return FLUID_FALLBACK[FLUID_CLASSES[idx]].slice();
  }
  function fluidEnvShift(durationMin, flags) {
    let shift = 0;
    if (flags.cool) shift -= 1;
    if (flags.hotHumid) shift += 1;
    if (durationMin >= 60 && durationMin <= 90 && !flags.hotHumid) shift -= 1;
    return clamp(shift, -1, 1);
  }

  function computeFluidSegment({ durationMin, tempC, humidityPct, sweat, bodyWeightKg, practicedFluidBand }) {
    const d = asNumber(durationMin) || 0;
    const flags = envFlags(tempC, humidityPct);
    const s = sweat || {};
    const measured = !!s.known && asNumber(s.rateLph) > 0;
    const similarity = s.similarity || 'similar';
    const fallbackClass = s.class || 'medium';

    if (d < 60) {
      const highMeasured = measured && asNumber(s.rateLph) >= 1.0;
      return {
        rangeMlph: null,
        display: flags.hotHumid || highMeasured ? 'Igény szerint; kis előre tervezett mennyiség indokolt lehet' : 'Igény szerint',
        confidence: measured ? (similarity === 'similar' ? 'MÉRT ADAT ALAPJÁN' : 'MÉRÉS + BECSLÉS') : 'BECSÜLT',
        measuredRateLph: measured ? asNumber(s.rateLph) : null,
        practicedTrigger: measured && asNumber(s.rateLph) > 1.4,
        warnings: ['60 perc alatt nem kényszerítünk merev ml/óra célt.']
      };
    }

    const shift = fluidEnvShift(d, flags);
    const fallback = shiftFallbackClass(fallbackClass, shift);
    let range, confidence, measuredPrimary = false;
    if (measured && similarity === 'similar') {
      const base = measuredFluidBase(s.rateLph, d);
      range = shiftMeasuredBand(base, shift);
      confidence = 'MÉRT ADAT ALAPJÁN';
      measuredPrimary = true;
    } else if (measured && similarity === 'partial') {
      const base = measuredFluidBase(s.rateLph, d);
      const mr = shiftMeasuredBand(base, shift);
      range = [Math.min(mr[0], fallback[0]), Math.max(mr[1], fallback[1])];
      confidence = 'MÉRT + BECSÜLT';
    } else {
      range = fallback;
      confidence = measured ? 'BECSÜLT – ELTÉRŐ MÉRÉS' : 'BECSÜLT';
    }

    if (!measuredPrimary && range[1] > 1000) range[1] = 1000;
    if (measuredPrimary) range[1] = Math.min(range[1], asNumber(s.rateLph) * 1000);

    const preliminaryHigh = range[1];
    const practicedTrigger = preliminaryHigh > 1000 || (measured && asNumber(s.rateLph) > 1.4);
    const practicedHigh = PRACTICED_FLUID_HIGH[practicedFluidBand || 'unknown'];
    const warnings = [];
    if (practicedTrigger) {
      if (practicedHigh !== null) range[1] = Math.min(range[1], practicedHigh);
      else range[1] = Math.min(range[1], 1000);
      warnings.push('Magas folyadékigény-jelzés: a végrehajtható felső tartományt a begyakorolt folyadékbevitel korlátozhatja.');
    }
    if (range[1] > 1000) warnings.push('1000 ml/óra feletti bevitel magas; csak begyakorolt és tolerált végrehajtással használd.');
    if (range[1] > 1250) warnings.push('1250 ml/óra feletti bevitel nagyon magas, kizárólag dokumentáltan begyakorolt végrehajtásnál jelenhet meg.');

    let deficitGuardrail = null;
    const weight = asNumber(bodyWeightKg);
    const rate = measured ? asNumber(s.rateLph) : null;
    if (rate && weight && d <= 240) {
      const durH = d / 60;
      const hiLph = range[1] / 1000;
      const deficitL = Math.max(0, rate - hiLph) * durH;
      const pct = deficitL / weight * 100;
      if (pct > 2) {
        const requiredMinLph = Math.max(0, rate - (0.02 * weight / durH));
        const requiredMin = Math.round(requiredMinLph * 1000);
        const capByMeasured = Math.round(rate * 1000);
        const capByPractice = practicedTrigger ? (practicedHigh ?? 1000) : Infinity;
        const achievable = Math.min(capByMeasured, capByPractice);
        range[0] = Math.min(Math.max(range[0], requiredMin), achievable);
        if (range[1] < range[0]) range[1] = range[0];
        deficitGuardrail = { projectedPct: pct, estimatedMinimumMlph: requiredMin, achievableMlph: achievable };
        warnings.push('A mért izzadási ráta alapján a jelenlegi terv >2% becsült testtömeg-deficitet jelezhet. Ez biztonsági figyelmeztetés, nem célérték. A céltartomány irányadó: nem kell erőltetni a folyadékbevitelt, ha az már kényelmetlen.');
      }
    }

    range = roundRange(range, 10);
    return {
      rangeMlph: range,
      display: `${range[0]}–${range[1]} ml/óra`,
      confidence,
      measuredRateLph: measured ? asNumber(s.rateLph) : null,
      measuredPrimary,
      practicedTrigger,
      deficitGuardrail,
      warnings
    };
  }

  function sweatSodiumBin(mmolL) {
    const v = asNumber(mmolL);
    if (v === null) return null;
    return v < 40 ? 'low' : v <= 60 ? 'mid' : 'high';
  }
  function sodiumReplacementFraction(bin, replacementRatio) {
    if (replacementRatio <= 0.70) return null;
    if (replacementRatio < 0.90) {
      if (bin === 'low') return null;
      return bin === 'mid' ? [0.20, 0.55] : [0.40, 0.70];
    }
    if (bin === 'low') return [0.40, 0.70];
    return bin === 'mid' ? [0.70, 0.80] : [0.80, 0.90];
  }
  function sodiumMeasurementConfidence(source) {
    if (source === 'professional') return 'MÉRT – MAGASABB BIZONYOSSÁG';
    if (source === 'consumer') return 'MÉRT – KORLÁTOZOTTABB BIZONYOSSÁG';
    return 'MÉRT ADAT ALAPJÁN';
  }

  function computeSodiumSegment({ segmentDurationMin, eligibilityDurationMin, fluid, sweat, sweatSodium }) {
    const seg = asNumber(segmentDurationMin) || 0;
    const elig = asNumber(eligibilityDurationMin ?? segmentDurationMin) || 0;
    if (seg <= 60) return { mode: 'NO_PLAN', rangeMgL: null, display: 'Nincs külön nátriumcél', secondary: '', confidence: 'NINCS KÜLÖN CÉL' };
    const standard = { mode: 'STANDARD', rangeMgL: [500, 700], display: '500–700 mg/L', confidence: 'ÁLTALÁNOS KIINDULÓ TARTOMÁNY' };
    if (!fluid?.rangeMlph) return standard;
    const sr = asNumber(sweat?.rateLph);
    const sodiumKnown = !!sweatSodium?.known && asNumber(sweatSodium?.mmolL) !== null;
    const midpointFluidLph = ((fluid.rangeMlph[0] + fluid.rangeMlph[1]) / 2) / 1000;
    const replacementRatio = sr ? midpointFluidLph / sr : null;
    const targetedContext = elig > 240 && sr !== null && sr < 1.8 && replacementRatio !== null && replacementRatio > 0.70 && replacementRatio < 1.0;
    const targetedEligible = targetedContext && sodiumKnown;
    if (!targetedEligible) {
      const mgph = [standard.rangeMgL[0] * fluid.rangeMlph[0] / 1000, standard.rangeMgL[1] * fluid.rangeMlph[1] / 1000];
      const interim = targetedContext && !sodiumKnown;
      return { ...standard, mode: interim ? 'STANDARD_INTERIM' : standard.mode, confidence: interim ? 'BECSÜLT KIINDULÓ TARTOMÁNY' : standard.confidence, secondaryRangeMgph: roundRange(mgph, 10), secondary: `${round10(mgph[0])}–${round10(mgph[1])} mg/óra a tervezett folyadékbevitel mellett`, replacementRatio };
    }
    const bin = sweatSodiumBin(sweatSodium.mmolL);
    const frac = sodiumReplacementFraction(bin, replacementRatio);
    if (!frac) {
      const mgph = [500 * fluid.rangeMlph[0] / 1000, 700 * fluid.rangeMlph[1] / 1000];
      return { mode: 'TARGETED_SEASON_TO_TASTE', rangeMgL: [500,700], display: '500–700 mg/L', confidence: sodiumMeasurementConfidence(sweatSodium?.source), secondaryRangeMgph: roundRange(mgph,10), secondary: `${round10(mgph[0])}–${round10(mgph[1])} mg/óra a tervezett folyadékbevitel mellett`, replacementRatio };
    }
    const sweatNaMgL = asNumber(sweatSodium.mmolL) * SODIUM_MG_PER_MMOL;
    const sodiumLossMgph = sr * sweatNaMgL;
    const fluidLowLph = fluid.rangeMlph[0] / 1000;
    const fluidHighLph = fluid.rangeMlph[1] / 1000;
    // Frozen G.6: low concentration uses sodium-intake low / fluid-execution HIGH;
    // high concentration uses sodium-intake high / fluid-execution LOW.
    const intakeMgph = [sodiumLossMgph * frac[0], sodiumLossMgph * frac[1]];
    const concentration = [intakeMgph[0] / fluidHighLph, intakeMgph[1] / fluidLowLph];
    const range = [Math.round(concentration[0]), Math.round(concentration[1])];
    const mgph = [intakeMgph[0], intakeMgph[1]];
    const nominalMgL = ((intakeMgph[0] + intakeMgph[1]) / 2) / midpointFluidLph;
    return {
      mode: 'TARGETED', rangeMgL: range, display: `${range[0]}–${range[1]} mg/L`,
      confidence: sodiumMeasurementConfidence(sweatSodium?.source), secondaryRangeMgph: [Math.round(mgph[0]),Math.round(mgph[1])],
      secondary: `${Math.round(mgph[0])}–${Math.round(mgph[1])} mg/óra a tervezett folyadékbevitel mellett`, replacementRatio,
      sweatSodiumMgL: Math.round(sweatNaMgL), replacementFraction: frac, nominalMgL: Math.round(nominalMgL),
      warning: replacementRatio > 0.95 ? 'Nagyon magas folyadékpótlási aránynál nem emeljük tovább automatikusan a nátriumcélt. Először a folyadéktervet érdemes felülvizsgálni.' : null
    };
  }

  function computeCaffeineDecision(input, durationMin) {
    const c = input || {};
    if (!c.enabled) return { requested: false, kind: 'OFF', planAllowed: false };
    if (!c.adult18) return { requested: true, kind: 'CORE_ONLY', planAllowed: false, reason: 'Automatikus koffeintervet csak 18 év felett készítünk.' };
    if (c.sensitive) return { requested: true, kind: 'CORE_ONLY', planAllowed: false, reason: 'Koffeinérzékenység jelölése miatt koffeinmentes tervet adunk.' };
    const weight = asNumber(c.weightKg);
    if (!weight || !c.priorTodayKnown || asNumber(c.priorTodayMg) === null) {
      return { requested: true, kind: 'NO_PLAN', planAllowed: false, reason: 'A koffeintervhez szükség van a testsúlyra és a mai eddigi koffeinbevitel legjobb becslésére.' };
    }
    const prior = Math.max(0, asNumber(c.priorTodayMg));
    const scienceLow = 2 * weight;
    const scienceHigh = 3 * weight;
    const dailyRemaining = Math.max(0, 400 - prior);
    const hardHigh = Math.min(scienceHigh, 200, dailyRemaining);
    const practice = asNumber(c.practicedTotalMg);
    const context = c.context || null;
    const isRace = context === 'race';
    if (hardHigh < scienceLow) {
      return { requested: true, kind: 'NO_PERFORMANCE_PLAN', planAllowed: false, scienceLow, scienceHigh, hardHigh, dailyRemaining, reason: 'A megadott adatok mellett nem adunk automatikus teljesítménycélú koffeintervet; a konzervatív korlátok elsőbbséget élveznek.' };
    }
    if (!practice || practice <= 0) {
      return { requested: true, kind: isRace ? 'NO_PRACTICE_RACE' : 'EDUCATION_ONLY', planAllowed: false, scienceLow, scienceHigh, hardHigh, dailyRemaining, reason: isRace ? 'Versenyre csak hasonló terhelésen begyakorolt koffeinmennyiségből készülhet automatikus terv.' : 'Még nincs hasonló terhelésen begyakorolt koffeinmennyiség. Előbb edzésen gyakorold; a kalkulátor nem ad automatikus első tesztdózist.' };
    }
    if (practice < scienceLow) {
      if (practice > hardHigh) return { requested: true, kind: 'NO_PLAN', planAllowed: false, scienceLow, scienceHigh, hardHigh, dailyRemaining, reason: 'A korábban használt mennyiség most nem fér bele az automatikus tervezés konzervatív korlátaiba.' };
      return { requested: true, kind: 'PRACTICED_BELOW_BAND', planAllowed: true, exactTotalMg: practice, scienceLow, scienceHigh, hardHigh, dailyRemaining, noPerformanceClaim: true, splitAllowed: durationMin > 120 && c.practicedMode === 'split', maxBlocks: durationMin > 120 && c.practicedMode === 'split' ? 2 : 1, sleep: sleepWarning(c.sleepWindowH) };
    }
    const high = Math.min(hardHigh, practice);
    if (high < scienceLow) return { requested: true, kind: 'NO_PERFORMANCE_PLAN', planAllowed: false, scienceLow, scienceHigh, hardHigh, dailyRemaining };
    return { requested: true, kind: 'PERFORMANCE_RANGE', planAllowed: true, rangeMg: [scienceLow, high], scienceLow, scienceHigh, hardHigh, dailyRemaining, splitAllowed: durationMin > 120 && c.practicedMode === 'split', maxBlocks: durationMin > 120 && c.practicedMode === 'split' ? 2 : 1, sleep: sleepWarning(c.sleepWindowH) };
  }

  function sleepWarning(value) {
    if (value === null || value === undefined || value === '' || value === 'unknown') return { level: 'caution', text: 'Az alvásig hátralévő idő ismeretlen; a késői koffeinbevitel ronthatja az alvást és a regenerációt.' };
    const h = asNumber(value);
    if (h !== null && h < 4) return { level: 'strong', text: 'Kevesebb mint 4 óra van az alvásig: erős alvás-/regenerációs figyelmeztetés. A terv nem épít késői koffeinpótlásra.' };
    if (h !== null && h <= 6) return { level: 'caution', text: '4–6 óra van az alvásig: a koffein ronthatja az alvást; a terv nem épít késői koffeinpótlásra.' };
    return { level: 'none', text: '' };
  }

  function candidateCarbPass(candidate, carb) {
    if (carb.kind === 'short') {
      const total = asNumber(candidate.totalCarbG);
      if (carb.totalRangeG[0] === 0 && carb.totalRangeG[1] === 0) return total === 0;
      return total !== null && total >= carb.totalRangeG[0] && total <= carb.totalRangeG[1];
    }
    const avg = asNumber(candidate.carbGph);
    if (avg === null) return false;
    if (carb.executionRangeGph) return avg >= carb.executionRangeGph[0] && avg <= carb.executionRangeGph[1];
    return Math.abs(avg - carb.executionGph) / carb.executionGph <= 0.05;
  }
  function rangeContains(range, value) { return !!range && Number.isFinite(value) && value >= range[0] && value <= range[1]; }

  function validateCoreCandidate(candidate, targets, input) {
    const hard = {
      SCOPE_PASS: candidate.scopePass !== false,
      QA_PASS: candidate.qaPass === true,
      CARB_PASS: candidateCarbPass(candidate, targets.carb),
      FLUID_PASS: !targets.fluid?.rangeMlph || rangeContains(targets.fluid.rangeMlph, asNumber(candidate.fluidMlph)),
      SODIUM_PASS: !targets.sodium?.rangeMgL || rangeContains(targets.sodium.rangeMgL, asNumber(candidate.sodiumMgL)),
      MTC_PASS: !targets.carb?.mtcRequired || candidate.mtcPass === true,
      DILUTION_PASS: candidate.dilutionPass !== false,
      DOSE_PASS: candidate.dosePass !== false,
      CAFFEINE_PASS: (asNumber(candidate.caffeineMg) || 0) === 0,
      SPORT_LEG_PASS: !candidate.supportedSports || candidate.supportedSports.includes(input.sport),
      TIMELINE_PASS: candidate.timelinePass !== false,
      ACCESSORY_PASS: candidate.accessoryPass !== false,
      STOCK_PASS: candidate.stockPass !== false,
      PRICE_PASS: candidate.pricePass !== false,
      VARIANT_PASS: candidate.variantPass !== false
    };
    return { hard, pass: Object.values(hard).every(Boolean) };
  }

  function nutritionDeviation(candidate, targets) {
    let score = 0;
    if (targets.carb?.executionGph) score += Math.abs(candidate.carbGph - targets.carb.executionGph) / targets.carb.executionGph;
    if (targets.fluid?.rangeMlph) {
      const mid = (targets.fluid.rangeMlph[0] + targets.fluid.rangeMlph[1]) / 2;
      score += Math.abs(candidate.fluidMlph - mid) / Math.max(1, mid);
    }
    if (targets.sodium?.rangeMgL) {
      const mid = (targets.sodium.rangeMgL[0] + targets.sodium.rangeMgL[1]) / 2;
      score += Math.abs(candidate.sodiumMgL - mid) / Math.max(1, mid);
    }
    return score;
  }
  function complexityVector(candidate) {
    const c = candidate.complexity || {};
    return [c.mixingOps || 0, c.productGroups || 0, c.irregularity || 0, c.eventsPerHour || 0, c.looseUnits || 0];
  }
  function cmpNum(a, b) { return (asNumber(a) || 0) - (asNumber(b) || 0); }
  function cmpVec(a, b) { for (let i=0;i<Math.max(a.length,b.length);i++){ const d=(a[i]||0)-(b[i]||0); if(d) return d;} return 0; }
  function coreComparator(route, targets, preference) {
    return (a, b) => {
      const dev = nutritionDeviation(a, targets) - nutritionDeviation(b, targets); if (Math.abs(dev) > 1e-9) return dev;
      if (route === 'cost') {
        let d = cmpNum(a.cartPriceCents,b.cartPriceCents); if(d) return d;
        d = cmpNum(a.eventUseValueCents,b.eventUseValueCents); if(d) return d;
        d = cmpNum(b.preferenceFit?.[preference],a.preferenceFit?.[preference]); if(d) return d;
        d = cmpVec(complexityVector(a),complexityVector(b)); if(d) return d;
        return cmpNum(b.stockRobustness,a.stockRobustness);
      }
      let d = cmpNum(b.preferenceFit?.[preference],a.preferenceFit?.[preference]); if(d) return d;
      d = cmpVec(complexityVector(a),complexityVector(b)); if(d) return d;
      d = cmpNum(a.cartPriceCents,b.cartPriceCents); if(d) return d;
      d = cmpNum(a.eventUseValueCents,b.eventUseValueCents); if(d) return d;
      return cmpNum(b.stockRobustness,a.stockRobustness);
    };
  }

  function overlayFeasible(option, decision, route) {
    const total = asNumber(option.totalMg);
    const blocks = asNumber(option.blocks) || 0;
    if (!Number.isFinite(total) || total <= 0 || total > decision.hardHigh || blocks > decision.maxBlocks) return false;
    if (blocks > 1 && !decision.splitAllowed) return false;
    if (route === 'pfh' && option.brandPurityPass === false) return false;
    if (option.timelinePass === false || option.validatorPass === false) return false;
    if (decision.kind === 'PRACTICED_BELOW_BAND') return total === decision.exactTotalMg;
    if (decision.kind === 'PERFORMANCE_RANGE') return total >= decision.rangeMg[0] && total <= decision.rangeMg[1];
    return false;
  }

  function applyCaffeineOverlayToPool(corePool, decision, route, targets, preference) {
    if (!decision?.planAllowed) return [];
    const expanded = [];
    for (const core of corePool) {
      for (const opt of core.caffeineOverlays || []) {
        if (!overlayFeasible(opt, decision, route)) continue;
        expanded.push({
          ...core,
          id: `${core.id}::${opt.id || opt.totalMg}`,
          caffeineOverlay: opt,
          caffeineMg: opt.totalMg,
          cartPriceCents: asNumber(opt.cartPriceCents) ?? core.cartPriceCents,
          eventUseValueCents: asNumber(opt.eventUseValueCents) ?? core.eventUseValueCents,
          shoppingList: opt.shoppingList || core.shoppingList,
          productionReleasePass: core.productionReleasePass === true && opt.productReleasePass === true
        });
      }
    }
    const baseCmp = coreComparator(route, targets, preference);
    expanded.sort((a,b) => {
      let d = cmpNum(a.caffeineMg,b.caffeineMg); if(d) return d;
      d = cmpNum(a.caffeineOverlay?.blocks,b.caffeineOverlay?.blocks); if(d) return d;
      d = cmpNum(a.caffeineOverlay?.productGroups,b.caffeineOverlay?.productGroups); if(d) return d;
      return baseCmp(a,b);
    });
    return expanded;
  }

  function resolveLiveShopping(candidate, runtimeContext) {
    const liveMap = runtimeContext?.liveProductMap;
    if (!candidate.shoppingList?.length) return { ready: false, shoppingList: candidate.shoppingList || [], reason: 'Ehhez a tervhez most nincs elérhető terméklista.' };
    if (!liveMap || typeof liveMap.get !== 'function') return { ready: false, shoppingList: candidate.shoppingList, reason: 'A termékek aktuális elérhetősége most nem ellenőrizhető.' };
    const out = [];
    for (const item of candidate.shoppingList) {
      const live = liveMap.get(String(item.sku));
      if (!live?.variantId || !live.available) return { ready:false, shoppingList:candidate.shoppingList, reason:`${item.sku}: az aktuális elérhetőség nem igazolható.` };
      out.push({ ...item, title: live.title || item.title, variantTitle: live.variantTitle || item.variantTitle, variantId: live.variantId, unitPriceCents: live.price });
    }
    return { ready: true, shoppingList: out };
  }

  function solveRoutes({ candidates, targets, input, caffeineDecision, runtimeContext }) {
    const all = Array.isArray(candidates) ? candidates : [];
    const routeResults = [];
    for (const route of ['tbp','cost','pfh']) {
      let pool = all.filter(c => c.route === route || (route !== 'pfh' && c.route === 'shared'));
      if (route === 'pfh') pool = pool.filter(c => c.pfhOnly === true || c.route === 'pfh');
      pool = pool.filter(c => validateCoreCandidate(c, targets, input).pass);
      if (!pool.length) continue;
      let selected;
      let caffeineUnavailable = false;
      if (caffeineDecision?.requested && caffeineDecision.kind !== 'OFF' && !['CORE_ONLY'].includes(caffeineDecision.kind)) {
        const overlays = applyCaffeineOverlayToPool(pool, caffeineDecision, route, targets, input.preference);
        if (overlays.length) selected = overlays[0];
        else caffeineUnavailable = true;
      }
      if (!selected) selected = pool.slice().sort(coreComparator(route, targets, input.preference))[0];
      const live = resolveLiveShopping(selected, runtimeContext);
      const customerReady = selected.productionReleasePass === true && live.ready && (!selected.caffeineOverlay || selected.caffeineOverlay.productReleasePass === true);
      const caff = selected.caffeineOverlay ? {
        enabled: true,
        totalMg: selected.caffeineOverlay.totalMg,
        blocks: selected.caffeineOverlay.blocks,
        noPerformanceClaim: caffeineDecision.kind === 'PRACTICED_BELOW_BAND',
        warningText: [caffeineDecision.sleep?.text, selected.caffeineOverlay.warningText].filter(Boolean).join(' ')
      } : { enabled: false, requestedButUnavailable: caffeineUnavailable };
      routeResults.push({
        id: `${route}-${selected.id}`,
        type: route,
        summary: selected.summary || (caffeineUnavailable ? 'A fő frissítési terv elkészült, de ehhez a termékkombinációhoz most nem adunk automatikus koffeines változatot.' : 'A terved célértékeit teljesítő végrehajtási kombináció.'),
        meta: [selected.meta, caffeineUnavailable ? 'A koffeinmentes fő terv változatlan.' : ''].filter(Boolean).join(' · '),
        cartPriceCents: selected.cartPriceCents,
        eventUseValueCents: selected.eventUseValueCents,
        shoppingList: live.shoppingList,
        customerReady,
        blockedLabel: customerReady ? '' : 'Még nem elérhető',
        caffeine: caff,
        internalCandidateId: selected.id
      });
    }
    return dedupeRoutes(routeResults);
  }

  function dedupeRoutes(routes) {
    const out = [];
    const sig = r => (r.shoppingList || []).map(x => `${x.sku}:${x.sellUnits || x.quantity}`).sort().join('|') + `|c${r.caffeine?.totalMg || 0}`;
    for (const r of routes) {
      const existing = out.find(x => sig(x) === sig(r));
      if (!existing) out.push(r);
      else {
        const badges = new Set([...(existing.badges || [])]);
        if (r.type === 'cost' || existing.type === 'cost') badges.add('Egyben a legalacsonyabb kosárárú terv');
        if (r.type === 'pfh' || existing.type === 'pfh') badges.add('PF&H rendszer');
        existing.badges = [...badges];
        existing.meta = [existing.meta, ...existing.badges].filter(Boolean).join(' · ');
      }
    }
    return out;
  }

  function buildDiscreteTimeline({ durationMin, discreteDoses, continuousCarbGph = 0, targetCarbGph = null }) {
    const d = asNumber(durationMin) || 0;
    if (!Array.isArray(discreteDoses) || !discreteDoses.length || d <= 0) return [];
    const totalDiscrete = discreteDoses.reduce((a,x)=>a+(asNumber(x.carbG)||0),0);
    const target = asNumber(targetCarbGph);
    const continuous = Math.max(0, asNumber(continuousCarbGph) || 0);
    // Frozen runtime: continuous carbohydrate starts at t=0 and is removed from the
    // cumulative target before discrete midpoint placement. If the caller already
    // supplies residual-only discrete doses, totalDiscrete remains the authority.
    const modelResidual = target !== null ? Math.max(0, (target - continuous) * (d / 60)) : totalDiscrete;
    const residualTarget = modelResidual > 0 ? modelResidual : totalDiscrete;
    if (!residualTarget || Math.abs(totalDiscrete - residualTarget) / residualTarget > 0.05) return [];
    const events = [];
    let cumulative = 0;
    let humanizeOffset = 0;
    for (let i=0;i<discreteDoses.length;i++) {
      const dose = discreteDoses[i];
      const g = asNumber(dose.carbG)||0;
      const midpoint = cumulative + g/2;
      const rawRounded = Math.round(((midpoint / residualTarget) * d) / 5) * 5;
      let t = rawRounded + humanizeOffset;
      if (d > 90 && continuous <= 0 && i === 0) {
        const humanized = clamp(rawRounded, 20, 30);
        humanizeOffset = humanized - rawRounded;
        t = humanized;
      }
      if (events.length && t - events[events.length-1].minute < 10) t = events[events.length-1].minute + 10;
      if (t >= d) return [];
      events.push({ minute:t, timeLabel:formatMinutes(t), text:dose.text || dose.title || `${g} g szénhidrát` });
      cumulative += g;
    }
    return events;
  }

  function resumeTimelineWithoutCatchup(events, missedIndex) {
    if (!Array.isArray(events)) return [];
    const i = Math.max(-1, Math.min(events.length - 1, Number(missedIndex)));
    // Missing a dose never doubles or shifts the next one. Return the untouched
    // future schedule so the UI can tell the athlete to resume ordinary rhythm.
    return events.slice(i + 1).map(e => ({ ...e }));
  }

  function plainWaterTopUp(fluidRangeMlph, preparedDrinkMlph) {
    if (!fluidRangeMlph) return null;
    const drink = Math.max(0, asNumber(preparedDrinkMlph) || 0);
    return [Math.max(0, fluidRangeMlph[0] - drink), Math.max(0, fluidRangeMlph[1] - drink)];
  }

  function resolveFlavorSelection({ quantity, variants, explicitVariantId = null }) {
    const qty = Math.max(1, Math.floor(asNumber(quantity) || 1));
    const vs = Array.isArray(variants) ? variants.filter(v => v && v.available !== false) : [];
    if (explicitVariantId !== null && explicitVariantId !== undefined) {
      const hit = vs.find(v => String(v.variantId) === String(explicitVariantId));
      return hit ? { status:'EXPLICIT', selections:[{ variantId:hit.variantId, quantity:qty }] } : { status:'BLOCKED', reason:'A választott variáns nem elérhető.' };
    }
    if (!vs.length) return { status:'BLOCKED', reason:'Nincs elérhető variáns.' };
    if (qty === 1) return { status:'CHOICE_REQUIRED', variants:vs };
    const mixable = vs.length > 1 && vs.every(v => v.autoMixEquivalent === true);
    if (!mixable) return { status:'CHOICE_REQUIRED', variants:vs, reason:'Ehhez a termékhez az ízt külön ki kell választanod.' };
    const selections = [];
    for (let i=0;i<qty;i++) {
      const v = vs[i % vs.length];
      const row = selections.find(x => String(x.variantId) === String(v.variantId));
      if (row) row.quantity += 1; else selections.push({ variantId:v.variantId, quantity:1 });
    }
    return { status:'AUTO_MIX', label:'Vegyesen', selections };
  }

  function nutritionTimeline(input, carb, fluid) {
    const out = [];
    if (carb.kind === 'short') {
      if (carb.totalRangeG?.[1] > 0) out.push({ timeLabel:'Terhelés eleje–közepe', text:`Opcionálisan ${carb.totalRangeG[0]}–${carb.totalRangeG[1]} g szénhidrát összesen, begyakorolt formában.` });
    } else {
      out.push({ timeLabel:'Szénhidrát', text:`Kezdd el a bevitelt a terhelés korai szakaszában, majd oszd el egyenletesen a ${carb.display} célt. A pontos ritmust a választott, már gyakorolt termékekhez igazítsd.` });
    }
    if (fluid?.rangeMlph) out.push({ timeLabel:'Folyadék', text:`Kiinduló céltartomány: ${fluid.display}. Igyál rendszeresen, kisebb adagokban. A céltartomány irányadó: nem kell erőltetni a folyadékbevitelt, ha az már kényelmetlen.` });
    else out.push({ timeLabel:'Folyadék', text:fluid?.display || 'Igény szerint.' });
    return out;
  }

  function totalsFromSegments(segments) {
    let carbLo=0, carbHi=0, fluidLo=0, fluidHi=0, sodiumLo=0, sodiumHi=0;
    for (const s of segments) {
      const h=(s.durationMin||0)/60;
      if (s.carb?.totalRangeG) { carbLo+=s.carb.totalRangeG[0]; carbHi+=s.carb.totalRangeG[1]; }
      else if (Number.isFinite(s.carb?.totalG)) { carbLo+=s.carb.totalG; carbHi+=s.carb.totalG; }
      if (s.fluid?.rangeMlph) { fluidLo+=s.fluid.rangeMlph[0]*h; fluidHi+=s.fluid.rangeMlph[1]*h; }
      if (s.sodium?.rangeMgL && s.fluid?.rangeMlph) {
        sodiumLo += s.sodium.rangeMgL[0] * (s.fluid.rangeMlph[0]/1000) * h;
        sodiumHi += s.sodium.rangeMgL[1] * (s.fluid.rangeMlph[1]/1000) * h;
      }
    }
    return {
      carb: carbLo===carbHi ? `${round5(carbLo)} g` : `${round5(carbLo)}–${round5(carbHi)} g`,
      fluid: `${round10(fluidLo)}–${round10(fluidHi)} ml`,
      sodium: `${round10(sodiumLo)}–${round10(sodiumHi)} mg`
    };
  }

  function singleSportPlan(input, runtimeContext) {
    const carb = computeCarb({ durationContextMin:input.durationMin, executionDurationMin:input.durationMin, intensity:input.intensity, practiced:input.practicedCarb });
    const fluid = computeFluidSegment({ durationMin:input.durationMin, tempC:input.tempC, humidityPct:input.humidityPct, sweat:input.sweat, bodyWeightKg:input.fluidBodyWeightKg, practicedFluidBand:input.practicedFluidBand });
    const sodium = computeSodiumSegment({ segmentDurationMin:input.durationMin, eligibilityDurationMin:input.durationMin, fluid, sweat:input.sweat, sweatSodium:input.sweatSodium });
    const targets={carb,fluid,sodium};
    const caffeineDecision=computeCaffeineDecision({...input.caffeine,context:input.context},input.durationMin);
    const candidates=runtimeContext?.patternLibrary || [];
    const routes=solveRoutes({candidates,targets,input,caffeineDecision,runtimeContext});
    const segments=[{durationMin:input.durationMin,carb,fluid,sodium}];
    return {
      version:VERSION,
      targets:{
        carb:{display:carb.display,range:carb.kind==='short'?'Rövid terhelésnél az óránkénti cél helyett opcionális teljes mennyiséget mutatunk.':`Céltartomány: ${carbEventRange(input.durationMin).join('–')} g/óra${carb.developmentTargetGph?` · Következő edzéses cél: ${carb.developmentTargetGph} g/óra`:''}`,confidence:carb.confidence},
        fluid:{display:fluid.display,range:fluid.deficitGuardrail?'A testsúlyadat alapján a túl alacsony folyadékbevitel külön figyelmeztetést kap.':'',confidence:fluid.confidence},
        sodium:{display:sodium.display,secondary:sodium.secondary,confidence:sodium.confidence}
      },
      legs:[], totals:totalsFromSegments(segments), timeline:nutritionTimeline(input,carb,fluid), routes,
      explanations:buildExplanations(carb,fluid,sodium,caffeineDecision,routes)
    };
  }

  function triSportPlan(input, runtimeContext) {
    const tri=input.tri||{};
    const eventDur=(tri.swimMin||0)+(tri.t1Min||0)+(tri.bikeMin||0)+(tri.t2Min||0)+(tri.runMin||0);
    const bikeCarb=computeCarb({durationContextMin:eventDur,executionDurationMin:tri.bikeMin,intensity:input.intensity,practiced:input.triPracticedCarb?.bike});
    const runCarb=computeCarb({durationContextMin:eventDur,executionDurationMin:tri.runMin,intensity:input.intensity,practiced:input.triPracticedCarb?.run});
    const bikeFluid=computeFluidSegment({durationMin:tri.bikeMin,tempC:input.tempC,humidityPct:input.humidityPct,sweat:input.triSweat?.bike||{},bodyWeightKg:input.fluidBodyWeightKg,practicedFluidBand:input.triPracticedFluid?.bike});
    const runFluid=computeFluidSegment({durationMin:tri.runMin,tempC:input.tempC,humidityPct:input.humidityPct,sweat:input.triSweat?.run||{},bodyWeightKg:input.fluidBodyWeightKg,practicedFluidBand:input.triPracticedFluid?.run});
    const bikeNa=computeSodiumSegment({segmentDurationMin:tri.bikeMin,eligibilityDurationMin:eventDur,fluid:bikeFluid,sweat:input.triSweat?.bike||{},sweatSodium:input.triSweatSodium?.bike||{}});
    const runNa=computeSodiumSegment({segmentDurationMin:tri.runMin,eligibilityDurationMin:eventDur,fluid:runFluid,sweat:input.triSweat?.run||{},sweatSodium:input.triSweatSodium?.run||{}});
    const segments=[{durationMin:tri.bikeMin,carb:bikeCarb,fluid:bikeFluid,sodium:bikeNa},{durationMin:tri.runMin,carb:runCarb,fluid:runFluid,sodium:runNa}];
    const caffeineDecision=computeCaffeineDecision({...input.caffeine,context:input.context},eventDur);
    // Production pattern libraries can supply tri-composite candidates. Until then, nutrition output remains valid and product routes stay gated.
    const triTargets={carb:{kind:'tri'},fluid:null,sodium:null};
    const routes=runtimeContext?.triPatternLibrary ? solveRoutes({candidates:runtimeContext.triPatternLibrary,targets:triTargets,input,caffeineDecision,runtimeContext}) : [];
    return {
      version:VERSION,
      targets:{
        carb:{display:`Kerékpár ${bikeCarb.display} · Futás ${runCarb.display}`,range:'Szakaszonkénti cél',confidence:'SZAKASZONKÉNTI TERV'},
        fluid:{display:`Kerékpár ${bikeFluid.display} · Futás ${runFluid.display}`,range:'Szakaszonkénti cél',confidence:'SZAKASZONKÉNTI TERV'},
        sodium:{display:`Kerékpár ${bikeNa.display} · Futás ${runNa.display}`,secondary:'Szakaszonként számolva',confidence:'SZAKASZONKÉNTI TERV'}
      },
      legs:[
        {label:'Úszás',carb:'0',fluid:'0',sodium:'0',confidence:'Úszás közben nem tervezünk terhelés alatti bevitelt.'},
        {label:'Kerékpár',carb:bikeCarb.display,fluid:bikeFluid.display,sodium:bikeNa.display,confidence:`${bikeCarb.confidence} · ${bikeFluid.confidence} · ${bikeNa.confidence}`},
        {label:'Futás',carb:runCarb.display,fluid:runFluid.display,sodium:runNa.display,confidence:`${runCarb.confidence} · ${runFluid.confidence} · ${runNa.confidence}`}
      ],
      totals:totalsFromSegments(segments),
      timeline:[
        {timeLabel:'Úszás',text:'Úszás közben nem tervezünk szénhidrát-, folyadék- vagy nátriumbevitelt. A kimaradt mennyiséget nem kell egyszerre bepótolni a kerékpár elején.'},
        {timeLabel:'Kerékpár',text:`Szénhidrát: ${bikeCarb.display}; folyadék: ${bikeFluid.display}; nátrium: ${bikeNa.display}. Kezdd korán, és oszd el egyenletesen a szakasz során.`},
        {timeLabel:'Futás',text:`Szénhidrát: ${runCarb.display}; folyadék: ${runFluid.display}; nátrium: ${runNa.display}. A futásra külön célt használunk; a kerékpáron begyakorolt bevitel nem kerül át automatikusan.`}
      ],
      routes,
      explanations:buildExplanations(null,null,null,caffeineDecision,routes).concat([{title:'Triatlon',text:'A teljes esemény hossza számít a kiinduló frissítési céloknál, de a kerékpáros és futó szakasz külön tervet kap. Az úszás alatt kimaradt bevitelt nem pótoljuk vissza egyszerre a kerékpár elején.'}])
    };
  }

  function buildExplanations(carb,fluid,sodium,caffeineDecision,routes) {
    const out=[];
    if(carb) out.push({title:'Szénhidrát',text:`A kiinduló célt az esemény hossza és intenzitása adja, majd a már begyakorolt szénhidrátbevitel korlátozhatja. ${carb.warning||''}`.trim()});
    if(fluid) out.push({title:'Folyadék',text:`${fluid.confidence}. A céltartomány a megadott körülményekből és az izzadási adat minőségéből indul ki. ${fluid.warnings?.join(' ')||'Edzésen ellenőrizd, hogyan működik a gyakorlatban, és igazítsd a szomjúságodhoz, gyomrodhoz és a valós körülményekhez.'}`});
    if(sodium) out.push({title:'Nátrium',text:`${sodium.confidence}. A nátriumcélt a folyadéktervedhez igazítjuk; nem cél a teljes becsült kiizzadt nátrium mechanikus visszapótlása. ${sodium.warning||''}`.trim()});
    if(caffeineDecision?.requested) {
      let text=caffeineDecision.reason||'';
      if(caffeineDecision.planAllowed&&caffeineDecision.kind==='PRACTICED_BELOW_BAND') text=`A korábban begyakorolt ${Math.round(caffeineDecision.exactTotalMg||0)} mg összmennyiséget nem emeljük meg automatikusan. Ezt begyakorolt végrehajtásként kezeljük, teljesítményígéret nélkül.`;
      else if(caffeineDecision.planAllowed&&caffeineDecision.rangeMg) text=`A megadott adatok alapján ${Math.round(caffeineDecision.rangeMg[0])}–${Math.round(caffeineDecision.rangeMg[1])} mg közötti automatikus tervezési tartomány fér bele. A rendszer az alacsonyabb megfelelő mennyiséget részesíti előnyben, és a teljes eseményre 200 mg-os automatikus plafont alkalmaz.`;
      if(caffeineDecision.sleep?.text) text=`${text} ${caffeineDecision.sleep.text}`.trim();
      out.push({title:'Koffein',text:text||'A koffein opcionális; a fő frissítési célokat nem módosítja.'});
    }
    return out;
  }

  async function run(input, runtimeContext={}) {
    if (!input || !input.sport) throw new Error('sport required');
    const normalized={...input};
    // Backward compatibility with v0.1 numeric field while v0.2 UI uses categorical practicedCarb.
    if(normalized.practicedCarb===undefined) normalized.practicedCarb=normalized.practicedCarbGph;
    return input.sport==='tri' ? triSportPlan(normalized,runtimeContext) : singleSportPlan(normalized,runtimeContext);
  }

  return {
    version: VERSION,
    run,
    preflightFluidTrigger: ({rateLph, preliminaryHighMlph}) => (asNumber(preliminaryHighMlph)||0)>1000 || (asNumber(rateLph)||0)>1.4,
    __test: {
      carbBase, parsePracticedCarb, computeCarb, envFlags, measuredFluidBase, computeFluidSegment,
      sweatSodiumBin, sodiumReplacementFraction, computeSodiumSegment, computeCaffeineDecision,
      validateCoreCandidate, solveRoutes, applyCaffeineOverlayToPool, buildDiscreteTimeline, dedupeRoutes,
      resumeTimelineWithoutCatchup, plainWaterTopUp, resolveFlavorSelection,
      singleSportPlan, triSportPlan, sleepWarning
    }
  };
});
